import { gql, useQuery } from '@apollo/client'
import { useMemo, useRef } from 'react'

import { type DeliverySourceType, type EmailSend, type Pagination } from '../../types'

type EmailSendData = Omit<EmailSend, 'id'>

type EmailSendsQueryData = {
  emailLogs: {
    data: EmailSendData[]
    pagination: Pagination
  }
}

type UseEmailSendsParams = {
  verbatim: string
  sourceType: DeliverySourceType | null
  sourceId?: string | null
}

const EMAIL_SENDS = gql`
  query EmailLogs($verbatim: String, $deliveryType: String, $sourceId: ID, $page: Int, $limit: Int) {
    emailLogs: mailingEmailLogs(
      verbatim: $verbatim
      delivery_type: $deliveryType
      source_id: $sourceId
      page: $page
      limit: $limit
    ) {
      data {
        user_firstname
        user_lastname
        user_email
        delivery_type
        delivery_template_code
        delivery_name
        sent_at
        error_reason
        status
      }
      pagination {
        current_page
        next_page
        has_next_page
        total
      }
    }
  }
`

const PAGE_SIZE = 20

const useEmailSends = ({ verbatim, sourceType, sourceId = null }: UseEmailSendsParams) => {
  const isLoadingMore = useRef(false)
  const variables = useMemo(() => {
    const sanitizedVerbatim = verbatim.trim()

    return {
      page: 1,
      limit: PAGE_SIZE,
      ...(sanitizedVerbatim && { verbatim: sanitizedVerbatim }),
      ...(sourceType && { deliveryType: sourceType }),
      ...(sourceId && { sourceId })
    }
  }, [sourceId, sourceType, verbatim])
  const { data, error, fetchMore, loading, networkStatus } = useQuery<EmailSendsQueryData>(EMAIL_SENDS, {
    variables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })
  const items = useMemo<EmailSend[]>(() => {
    return (data?.emailLogs.data ?? []).map((emailSend, index) => ({
      ...emailSend,
      id: ['email-send', index, emailSend.user_email, emailSend.delivery_template_code].filter(Boolean).join('-')
    }))
  }, [data])

  const fetchNextPage = async () => {
    if (networkStatus === 3 || isLoadingMore.current) {
      return
    }

    const nextPage = data?.emailLogs.pagination.next_page

    if (!nextPage) {
      return
    }

    try {
      isLoadingMore.current = true

      await fetchMore({
        variables: {
          ...variables,
          page: nextPage
        }
      })
    } catch (fetchError) {
      console.error('Error fetching the next email sends page:', fetchError)
    } finally {
      isLoadingMore.current = false
    }
  }

  return {
    error,
    fetchNextPage,
    items,
    loading: (loading && networkStatus !== 3) || networkStatus === 2,
    loadingNext: networkStatus === 3,
    shouldFetchNextPage: Boolean(!loading && !isLoadingMore.current && data?.emailLogs.pagination.has_next_page),
    total: data?.emailLogs.pagination.total ?? 0
  }
}

export default useEmailSends
